'use client';

import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import { useAtomValue, useSetAtom } from 'jotai';
import Link from 'next/link';

import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { tenantAtom } from '@core/Atoms/Tenant/Tenant.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { currentVehicleIdAtom } from '@core/Atoms/Vehicle/CurrentVehicleId.atom';
import type { LeadFormId } from '@core/Entities/Leads/LeadFormId';
import Button from '@gds/Button/Button';
import Logo from '@gds/Logo/Logo';
import { Typography } from '@gds/Typography/Typography';
import { experimentsAtom } from '@growthBookExperimentation/Atoms/Experiments.atom';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { leadFormDataAtom } from '@lead-forms/Components/LeadFormData/LeadFormData.atom';
import type { LeadType } from '@lead-forms/Entities/LeadType.entity';
import { useCallDealer } from '@lead-forms/Hooks/UseCallDealer';
import { generateLeadTrackingData } from '@lead-forms/Tracking/LeadForms.tracking';
import { CallDealerCTA } from '@pdp/Components/CallDealer/CallDealerCTA';
import { TestDriveCta } from '@pdp/Components/TestDriveCta/TestDriveCta';
import { generateCheckoutStartTrackingData } from '@tracking/LeadCTA.tracking';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';
import { RacLogo } from '@whitelabel/Components/RacLogo';

import { getLeadCtasForDisplay, getNumOfCTAs } from './LeadCTAHelpers';
import type { LeadCTAsProps } from './LeadCTAs.entity';
import { leadCtasConfig } from './LeadCtaConfig';

import styles from './LeadCTAs.module.css';

export const LeadCTAs = ({
  vehicle,
  strings: { pdpStrings: messages, leadFormMessages, testDriveCtaStrs },
  isEcom,
  locale,
  hasInactiveStatus,
  isSticky: isStickyCta,
  hasStartedAutoGenerateNo,
}: LeadCTAsProps) => {
  const leadCTAsRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(false);
  const flags = useAtomValue(flagsAtom);

  useEffect(() => {
    setIsReady(true);
    const headerHeight = '-75'; // Fixed header height for mobile
    const options = {
      root: null,
      rootMargin: `${headerHeight}px 0px 0px 0px`,
      threshold: 0,
    };

    const observer = new IntersectionObserver(entries => {
      const query = window.matchMedia(`(max-width: 767px)`);
      const isIntersecting = entries[0].isIntersecting;
      setIsSticky(!isIntersecting && query.matches);
    }, options);

    const targetElement = flags.stickyCta2PdpMobileExperiment
      ? document.getElementById('sticky-info')
      : leadCTAsRef.current;

    if (targetElement) {
      observer.observe(targetElement);
    }

    return () => {
      if (targetElement) {
        observer.unobserve(targetElement);
      }
    };
  }, []);

  const tenant = useAtomValue(tenantAtom);
  const experiments = useAtomValue(experimentsAtom);
  const listingId = useAtomValue(currentVehicleIdAtom) || vehicle?.heycarId || null;

  const isVsp = vehicle?.spec?.d2cModel === 'vsp';
  const isInvestorStockLayout = tenant !== 'rac' && vehicle?.spec?.d2c && isEcom;
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);
  const { shouldAutoGenNumber } = useCallDealer();
  const toggleLeadForm = useSetAtom(activeLeadFormAtom);

  const formData = useAtomValue(leadFormDataAtom('message-dealer' as LeadFormId));

  const ctasToDisplay = getLeadCtasForDisplay(leadCtasConfig, flags, isEcom, vehicle);

  const onLeadModal = (leadId: LeadType) => {
    const {
      leadStart: { event, context },
    } = generateLeadTrackingData({
      searchParams,
      userId,
      formState,
      leadFormId: leadId,
      leadId: '',
      listingId,
      vehicle,
      ctaLocation: 'pdp',
      experiments,
      leadAction: '',
      financeQuoteChecked: formData.financeQuote.financeQuoteChecked || false,
    });

    trackCustomEvent({ event, context });

    //Call dealer has its own buttons and handlers
    //not sure if we still need this check, but leaving it in for safety
    if (leadId !== 'call-dealer') {
      toggleLeadForm(leadId);
    }
  };

  const handleClickCheckout = () => {
    const {
      checkoutStart: { event, context },
    } = generateCheckoutStartTrackingData({
      searchParams,
      userId,
      formState,
      listingId,
      vehicle,
      ctaLocation: 'pdp',
    });
    trackCustomEvent({ event, context });
  };

  const shouldShowStickyCta = isSticky || (isSticky && isInvestorStockLayout);

  const numOfCtas = getNumOfCTAs(ctasToDisplay, hasInactiveStatus, isEcom);

  return (
    <div
      ref={leadCTAsRef}
      className={`${shouldShowStickyCta ? styles.minHeightZero : styles.leadButtonsOuter} ${
        styles[`btnNum${numOfCtas}`]
      }`}
    >
      <div
        className={cn(styles.leadButtonsWrapper, {
          [styles.stickyContent]: shouldShowStickyCta,
          [styles.stickyCta]: isStickyCta,
          [styles.vspAndNoneVspMobileCTAWrapper]: isVsp || isEcom,
          [styles.vspDesktopLayout]: isInvestorStockLayout,
        })}
      >
        {hasInactiveStatus ? (
          <Button disabled>{messages.inActiveVehicleLabel}</Button>
        ) : (
          <>
            {isEcom && (
              <>
                {tenant === 'rac' && (
                  <div className={styles.teamedWith}>
                    <RacLogo style={{ fontSize: 100 }} />
                    <Typography Component="span">{messages.buyOnlineWith}</Typography>
                    <Logo usage="light_bg" fontSize={80} />
                  </div>
                )}

                <Link
                  data-is-legacy-link
                  href={`/uk/checkout/${vehicle?.heycarId}/about-you`}
                  className={styles.buyOnlineCTA}
                >
                  <Button
                    data-test-id="buy-online-cta"
                    className={shouldShowStickyCta ? styles.priorityCTA : ''}
                    onClick={handleClickCheckout}
                    disabled={!isReady}
                  >
                    {messages.buyOnline}
                  </Button>
                </Link>
              </>
            )}

            {ctasToDisplay.map(([id, data]) => {
              const isEcomAndSticky = isEcom && shouldShowStickyCta;
              const hasButtonText = data.showButtonText?.({
                isSticky: shouldShowStickyCta,
                isEcom,
                isVsp,
              });
              return (
                <Button
                  leftIcon={isEcomAndSticky ? data.buttonIcon : null}
                  data-test-id={isReady ? data.labelId() : ''}
                  key={id}
                  variant={data.variant({ isEcom, flags, vehicle })}
                  onClick={() => onLeadModal(id as LeadType)}
                  disabled={!isReady}
                  className={`${
                    id === 'message-dealer' && !isEcom && shouldShowStickyCta
                      ? styles.priorityCTA
                      : ''
                  }`}
                >
                  {!isInvestorStockLayout && hasButtonText
                    ? messages[data.labelId(flags)]
                    : isInvestorStockLayout && hasButtonText && id !== 'request-callback'
                      ? messages.message
                      : isInvestorStockLayout && hasButtonText && id === 'request-callback'
                        ? messages.call
                        : ''}
                </Button>
              );
            })}

            <CallDealerCTA
              strings={{ ...messages, ...leadFormMessages }}
              isSticky={shouldShowStickyCta}
              isEcom={isEcom}
              disabled={!isReady}
              shouldAutoGenNumber={shouldAutoGenNumber}
              hasStartedAutoGenerateNo={hasStartedAutoGenerateNo}
            />
          </>
        )}

        {flags.testDriveCta && (
          <TestDriveCta vehicle={vehicle} isReady={isReady} strings={testDriveCtaStrs} />
        )}
      </div>
    </div>
  );
};
